import { addDays, format, UTCDate } from '@/utils/dates';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { FinancialServiceProviderAdapters, LegalEntityCountry } from '@seapoint/server/enums';
import { AlertCircle, Dot } from 'lucide-react';
import { ComponentProps } from 'react';
import { Button } from '../../../../../design-system/button/button';
import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';

// https://docs.yapily.com/pages/tools-and-services/yapilyconnect/yapilyconnect-ux-ais-guidelines/#introduction
export const GB_CONSENT_DURATION_DAYS = 90;
export const EEA_CONSENT_DURATION_DAYS = 180;

const TODAY = new UTCDate();
const DATE_FORMAT = 'dd/MM/yyyy';

// https://docs.yapily.com/pages/tools-and-services/yapilyconnect/yapilyconnect-ux-ais-guidelines/#step-3-display-the-account-information-request-summary
const YAPILY_TERMS_AND_CONDITIONS_URL = 'https://www.yapily.com/legal/end-user-terms';
const YAPILY_PRIVACY_NOTICE_URL = 'https://www.yapily.com/legal/privacy-policy';

// GoCardless legal links
const GOCARDLESS_TERMS_AND_CONDITIONS_URL = 'https://gocardless.com/legal/end-user-agreement/';
const GOCARDLESS_PRIVACY_NOTICE_URL = 'https://gocardless.com/legal/privacy/';

type ConnectAccountDialogProps = {
  isOpen: boolean;
  closePopup: () => void;
  institutionName: string;
  institutionLogo: string;
  country: LegalEntityCountry;
  isFetchingConnectionLink: boolean;
  isInstitutionConnected: boolean;
  connectionError?: Error;
  onConnectAccounts: () => void;
  psuIdProvided?: boolean;
  provider?: FinancialServiceProviderAdapters;
};

export const ConnectAccountDialog = ({
  isOpen,
  closePopup,
  institutionName,
  institutionLogo,
  country,
  isFetchingConnectionLink,
  isInstitutionConnected,
  connectionError,
  onConnectAccounts,
  psuIdProvided = false,
  provider = FinancialServiceProviderAdapters.YAPILY,
}: ConnectAccountDialogProps) => {
  const { descriptionElement, ctaText, onCtaClick } = getCtaContent({
    onConnectAccounts,
    country,
    institutionName,
    psuIdProvided,
    provider,
  });

  return (
    <Dialog open={isOpen} onClose={closePopup} transition>
      <DialogBackdrop className="fixed inset-0 bg-black/70" />

      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        <DialogPanel className="relative flex w-full max-w-sm flex-col overflow-hidden rounded-xl bg-white">
          <ConnectionStatusBadge
            isInstitutionConnected={isInstitutionConnected}
            hasConnectionError={!!connectionError}
          />

          <div className="flex justify-start gap-3 p-5">
            <img src={institutionLogo} alt="" loading="eager" fetchpriority="high" className="h-12 w-12 rounded-full" />

            <div className="flex flex-col justify-start gap-1">
              <DialogTitle as="h3" className="text-sm leading-5 font-medium text-black">
                {institutionName}
              </DialogTitle>

              {descriptionElement}
            </div>
          </div>

          {connectionError && (
            <div className="mx-5 mb-3 flex gap-2 rounded-lg bg-red-50 p-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              <div>
                <p className="text-xs font-medium text-red-900">Connection Failed</p>
                <p className="mt-1 text-xs text-red-700">{connectionError.message}</p>
              </div>
            </div>
          )}

          <Inline space={2} className="bg-accent p-5">
            {isFetchingConnectionLink ? (
              <Button isLoading={true} className="flex-1">
                Connecting the Bank...
              </Button>
            ) : (
              <Button onClick={onCtaClick} className="flex-1" disabled={!!connectionError}>
                {ctaText}
              </Button>
            )}

            <Button variant="outline" onClick={closePopup} disabled={isFetchingConnectionLink}>
              Cancel
            </Button>
          </Inline>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

type GetCtaContentProps = {
  onConnectAccounts: () => void;
  country: ComponentProps<typeof ConnectAccountDialog>['country'];
  institutionName: ComponentProps<typeof ConnectAccountDialog>['institutionName'];
  psuIdProvided?: boolean;
  provider: FinancialServiceProviderAdapters;
};

const getCtaContent = ({
  onConnectAccounts,
  country,
  institutionName,
  psuIdProvided,
  provider,
}: GetCtaContentProps) => {
  const isGoCardless = provider === FinancialServiceProviderAdapters.GOCARDLESS;
  const isStripe = provider === FinancialServiceProviderAdapters.STRIPE;
  const providerName = isGoCardless ? 'GoCardless' : isStripe ? 'Seapoint' : 'Yapily Connect';
  const termsUrl = isGoCardless ? GOCARDLESS_TERMS_AND_CONDITIONS_URL : YAPILY_TERMS_AND_CONDITIONS_URL;
  const privacyUrl = isGoCardless ? GOCARDLESS_PRIVACY_NOTICE_URL : YAPILY_PRIVACY_NOTICE_URL;
  if (country === LegalEntityCountry.GB) {
    const expirationDateGB = addDays(TODAY, GB_CONSENT_DURATION_DAYS);
    const formattedExpirationDateGB = format(expirationDateGB, DATE_FORMAT);

    return {
      descriptionElement: (
        <Stack space={3}>
          {!isStripe && (
            <p className="text-xs text-muted-foreground">
              {psuIdProvided
                ? `We're ready to connect your ${institutionName} account with the details you provided. We have partnered with ${providerName} to access your bank data${isGoCardless ? '' : ' and act as their agent when we share this information back to you'}.`
                : `We have partnered with ${providerName} to access your bank data at ${institutionName}${isGoCardless ? '' : ' and act as their agent when we share this information back to you'}.`}
            </p>
          )}

          <p className="text-xs text-muted-foreground">
            {`You will now be securely redirected to ${institutionName} to give access to the following information:`}
          </p>

          <ul className="list-inside list-disc">
            <li className="text-xs text-muted-foreground">Account(s) details</li>
            <li className="text-xs text-muted-foreground">Balances</li>
            <li className="text-xs text-muted-foreground">Transaction history</li>
          </ul>

          <p className="text-xs text-muted-foreground">
            By using the service, you agree to {providerName} accessing your {isStripe ? 'Stripe' : 'bank'} data
            {!isStripe && (
              <>
                , the{' '}
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-muted-foreground"
                >
                  Terms & Conditions
                </a>{' '}
                and{' '}
                <a
                  href={privacyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-muted-foreground"
                >
                  Privacy Notice
                </a>
              </>
            )}
            {isStripe && (
              <>
                , the{' '}
                <a href="#" target="_blank" rel="noopener noreferrer" className="underline hover:text-muted-foreground">
                  Terms & Conditions
                </a>{' '}
                and{' '}
                <a href="#" target="_blank" rel="noopener noreferrer" className="underline hover:text-muted-foreground">
                  Privacy Notice
                </a>
              </>
            )}
            .
          </p>

          <p className="text-xs text-muted-foreground">
            {`
            This consent will automatically expire on ${formattedExpirationDateGB}.`}
          </p>
        </Stack>
      ),
      ctaText: 'Confirm',
      onCtaClick: onConnectAccounts,
    };
  }

  const expirationDateEEA = addDays(TODAY, EEA_CONSENT_DURATION_DAYS);
  const formattedExpirationDateEEA = format(expirationDateEEA, DATE_FORMAT);

  return {
    descriptionElement: (
      <Stack space={3}>
        {!isStripe && (
          <p className="text-xs text-muted-foreground">
            {psuIdProvided
              ? `We're ready to connect your ${institutionName} account with the details you provided. We have partnered with ${providerName} to access your bank data.`
              : `We have partnered with ${providerName} to access your bank data at ${institutionName}.`}
          </p>
        )}

        <p className="text-xs text-muted-foreground">
          {`You will now be securely redirected to ${institutionName} to give access to the following information:`}
        </p>

        <ul className="list-inside list-disc">
          <li className="text-xs text-muted-foreground">Account(s) details</li>
          <li className="text-xs text-muted-foreground">Balances</li>
          <li className="text-xs text-muted-foreground">Transaction history</li>
        </ul>

        <p className="text-xs text-muted-foreground">
          By using the service, you agree to {providerName} accessing your {isStripe ? 'Stripe' : 'bank'} data
          {!isStripe && (
            <>
              , the{' '}
              <a
                href={termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-muted-foreground"
              >
                Terms & Conditions
              </a>{' '}
              and{' '}
              <a
                href={privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-muted-foreground"
              >
                Privacy Notice
              </a>
            </>
          )}
          {isStripe && (
            <>
              , the{' '}
              <a href="#" target="_blank" rel="noopener noreferrer" className="underline hover:text-muted-foreground">
                Terms & Conditions
              </a>{' '}
              and{' '}
              <a href="#" target="_blank" rel="noopener noreferrer" className="underline hover:text-muted-foreground">
                Privacy Notice
              </a>
            </>
          )}
          .
        </p>

        <p className="text-xs text-muted-foreground">{`This consent will be valid until ${formattedExpirationDateEEA}.`}</p>
      </Stack>
    ),
    ctaText: 'Confirm',
    onCtaClick: onConnectAccounts,
  };
};

type ConnectionStatusBadgeProps = {
  isInstitutionConnected: boolean;
  hasConnectionError: boolean;
};

const ConnectionStatusBadge = ({ isInstitutionConnected, hasConnectionError }: ConnectionStatusBadgeProps) => {
  if (!isInstitutionConnected && !hasConnectionError) {
    return null;
  }

  const variant = isInstitutionConnected ? 'success' : 'error';
  const { badgeText, iconColor } = getBadgeContent(variant);

  return (
    <div className="absolute top-2 right-4 flex items-center">
      <Dot color={iconColor} size={24} />

      <p className="text-[10px] text-black">{badgeText}</p>
    </div>
  );
};

const getBadgeContent = (variant: 'success' | 'error') => {
  switch (variant) {
    case 'success':
      return { badgeText: 'Connected', iconColor: '#82CC69' };
    case 'error':
      return { badgeText: 'Connection error', iconColor: '#FF5318' };
    default:
      return exhaustiveCheck(variant);
  }
};

// Default argument for switch statements so TypeScript notifies if we fail to cover a case
const exhaustiveCheck = (arg: never) => arg;
